import { useState, useEffect } from 'react';
import { Gift, Star, TrendingUp, Coins, Trophy, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { formatDistanceToNow } from 'date-fns';

export default function LoyaltyPoints() {
  const [data, setData] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    Promise.all([
      api.get('/loyalty/my-points'),
      api.get('/loyalty/leaderboard'),
    ]).then(([p, l]) => {
      setData(p.data);
      setLeaderboard(l.data.leaderboard || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center items-center min-h-screen"><div className="spinner" /></div>;

  const points = data?.points || {};
  const history = data?.history || [];

  const TYPE_CONFIG = {
    earned:   { color: '#00C864', label: 'Earned', sign: '+' },
    redeemed: { color: '#FF6B00', label: 'Redeemed', sign: '-' },
    bonus:    { color: '#F5A623', label: 'Bonus', sign: '+' },
    expired:  { color: '#6B6B8A', label: 'Expired', sign: '-' },
    adjusted: { color: '#4DA2FF', label: 'Adjusted', sign: '' },
  };

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-2xl mx-auto">
      {/* Header */}
      <div className="mt-4 mb-6 flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#FF6B00,#F5A623)' }}>
          <Gift size={22} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Sora' }}>Reward Points</h1>
          <p className="text-xs text-brand-muted">Every $1 spent = 1 point · Future blockchain coin</p>
        </div>
      </div>

      {/* Balance card */}
      <div className="rounded-2xl p-5 mb-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#FF6B00,#F5A623)' }}>
        <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: 'white', transform: 'translate(30%,-30%)' }} />
        <p className="text-white/80 text-sm font-medium mb-1">Available Points</p>
        <div className="flex items-end gap-2 mb-3">
          <span className="text-5xl font-bold text-white" style={{ fontFamily: 'Sora' }}>
            {(points.available || 0).toLocaleString()}
          </span>
          <span className="text-white/70 text-lg mb-1">pts</span>
        </div>
        <div className="flex gap-4 text-sm">
          <div>
            <p className="text-white/60 text-xs">USD Value</p>
            <p className="text-white font-bold">${points.valueUSD || '0.00'}</p>
          </div>
          <div>
            <p className="text-white/60 text-xs">Lifetime Earned</p>
            <p className="text-white font-bold">{(points.lifetimeEarned || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-white/60 text-xs">Total Spent</p>
            <p className="text-white font-bold">${(points.lifetimeSpentUSD || 0).toFixed(0)}</p>
          </div>
        </div>
      </div>

      {/* Future coin conversion banner */}
      <div className="p-4 rounded-2xl mb-6" style={{ background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.25)' }}>
        <div className="flex items-start gap-3">
          <Coins size={20} className="text-brand-orange flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-sm text-brand-orange mb-1">🚀 Coming Soon — CryptoMart Coin</p>
            <p className="text-xs text-brand-muted leading-relaxed">
              Your {(points.available || 0).toLocaleString()} points will convert <strong className="text-white">1:1</strong> to CryptoMart coins when we launch our blockchain. Keep spending to earn more points now!
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 p-1 rounded-xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
        {[['overview','Overview'],['history','History'],['leaderboard','🏆 Top Buyers']].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex-1 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: tab===id?'linear-gradient(135deg,#FF6B00,#F5A623)':'transparent', color: tab===id?'white':'#6B6B8A' }}>
            {label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="space-y-3">
          <div className="p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <h3 className="font-bold mb-3" style={{ fontFamily: 'Sora' }}>How Points Work</h3>
            <div className="space-y-3">
              {[
                [Star, '#F5A623', 'Earn points', 'Every $1 you spend = 1 point automatically added after delivery'],
                [Gift, '#FF6B00', 'Redeem points', 'Use 100 points = $1 discount on your next purchase (max $5/order)'],
                [Coins, '#9945FF', 'Convert to coin', 'When CryptoMart launches its blockchain, your points become real coins 1:1'],
                [TrendingUp, '#00C864', 'No expiry', 'Your points never expire — keep accumulating until the coin launch'],
              ].map(([Icon, color, title, desc]) => (
                <div key={title} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}15` }}>
                    <Icon size={16} style={{ color }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{title}</p>
                    <p className="text-xs text-brand-muted">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* HISTORY */}
      {tab === 'history' && (
        <div>
          {history.length === 0 ? (
            <div className="text-center py-16 text-brand-muted">
              <Gift size={40} className="mx-auto mb-3 opacity-30" />
              <p>No points history yet. Start shopping to earn points!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {history.map((item, i) => {
                const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.earned;
                return (
                  <div key={i} className="flex items-center justify-between p-3 rounded-xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
                    <div>
                      <p className="text-sm font-medium">{item.description}</p>
                      <p className="text-xs text-brand-muted">{item.createdAt ? formatDistanceToNow(new Date(item.createdAt), { addSuffix: true }) : ''}</p>
                    </div>
                    <span className="font-bold font-mono text-sm" style={{ color: cfg.color }}>
                      {cfg.sign}{Math.abs(item.points)} pts
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* LEADERBOARD */}
      {tab === 'leaderboard' && (
        <div className="space-y-2">
          {leaderboard.map((entry, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: '#12121A', border: `1px solid ${i === 0 ? '#F5A623' : '#1E1E2E'}` }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0"
                style={{ background: i === 0 ? '#F5A623' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#1E1E2E', color: i < 3 ? 'black' : '#6B6B8A' }}>
                {i + 1}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">{entry.username}</p>
                <p className="text-xs text-brand-muted">{entry.country} · Spent {entry.lifetimeSpent}</p>
              </div>
              <div className="text-right">
                <p className="font-bold font-mono text-sm" style={{ color: '#FF6B00' }}>{entry.points.toLocaleString()}</p>
                <p className="text-xs text-brand-muted">points</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
